/**
 * Page publique de signature de l'AoH CEE — sans auth CRM.
 * Accédée via /sign/aoh/:token (lien envoyé par email).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, RefreshCw, ShieldCheck, FileText, Eye,
  AlertTriangle,
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const fmtEUR = (n) => n == null ? '—'
  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const fmtKwh = (n) => n == null ? '—'
  : `${new Intl.NumberFormat('fr-FR').format(Math.round(n))} kWh cumac`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; }
};

export default function SignAoh() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [name, setName] = useState('');
  const [qualite, setQualite] = useState('');
  const [place, setPlace] = useState('');
  const [accept, setAccept] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signedOk, setSignedOk] = useState(null);
  const [showPdf, setShowPdf] = useState(false);

  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { document.title = 'Signature AoH — Reno Resolve'; }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/cee/aoh/sign/${token}`);
      setState({ loading: false, data: r.data, error: null });
      if (r.data.status === 'signed') {
        setSignedOk({ signed_at: r.data.signed_at, signed_pdf_url: r.data.signed_pdf_url });
      }
    } catch (e) {
      setState({
        loading: false, data: null,
        error: e?.response?.data?.detail || 'Lien invalide ou expiré',
      });
    }
  };

  // Init canvas whenever it becomes visible
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || signedOk) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0a1a3b';
    ctx.lineWidth = 2.2;
  }, [state.data, signedOk]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const cx = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const cy = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return { x: cx, y: cy };
  };
  const startDraw = (e) => { e.preventDefault(); drawing.current = true; last.current = getPos(e); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    last.current = { x, y };
  };
  const stopDraw = () => { drawing.current = false; };
  const clearSig = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
  };
  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < data.length; i += 4) if (data[i] !== 0) return false;
    return true;
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Renseignez votre nom'); return; }
    if (!accept) { toast.error('Vous devez accepter les déclarations sur l\'honneur'); return; }
    if (isCanvasEmpty()) { toast.error('Apposez votre signature dans le cadre'); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const r = await axios.post(`${API_BASE}/cee/aoh/sign/${token}`, {
        signature_data_url: dataUrl,
        nom_signataire: name.trim(),
        qualite_signataire: qualite.trim() || 'Bénéficiaire',
        place: place.trim(),
        accepte_declarations: true,
      });
      if (r.data?.success) {
        setSignedOk({
          signed_at: r.data.signed_at,
          signed_pdf_url: r.data.signed_pdf_url,
        });
        toast.success('AoH signée avec succès');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally { setSubmitting(false); }
  };

  // Loading / error / signed-success screens
  if (state.loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004AAD]" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-200 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-900 mb-1">Lien invalide</h2>
          <p className="text-slate-500 text-sm">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const signUrl = `${API_BASE}/cee/aoh/preview/${token}.pdf`;
  const signedUrl = signedOk?.signed_pdf_url
    ? `${process.env.REACT_APP_BACKEND_URL}${signedOk.signed_pdf_url}`
    : null;

  if (signedOk) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div
          className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 max-w-lg text-center"
          data-testid="aoh-signed-success"
        >
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Attestation signée !
          </h2>
          <p className="text-slate-600 text-sm mb-1">
            Merci — votre attestation sur l'honneur CEE a bien été enregistrée.
          </p>
          <p className="text-slate-400 text-xs mb-6">
            Signée le {new Date(signedOk.signed_at).toLocaleString('fr-FR')}
          </p>
          {signedUrl && (
            <a href={signedUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#004AAD] text-white rounded-lg font-medium hover:bg-[#003a8a]"
               data-testid="aoh-download-signed">
              <FileText className="w-4 h-4" /> Télécharger le PDF signé
            </a>
          )}
          <p className="text-[11px] text-slate-400 mt-6">
            Une copie du document signé horodaté vous a également été envoyée par email.
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Signature form
  // ---------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      {/* Header brand */}
      <div className="bg-[#004AAD] text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7" />
          <div>
            <h1 className="text-xl font-bold">Attestation sur l'Honneur — CEE</h1>
            <p className="text-blue-100 text-sm">
              Dossier {d.dossier_id} · Fiche {d.fiche_code} v{d.fiche_version}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {/* Résumé opération */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="aoh-summary">
          <div className="grid sm:grid-cols-3 gap-4">
            <SummaryItem label="Bénéficiaire" value={d.beneficiaire?.nom || '—'} />
            <SummaryItem label="Adresse chantier"
              value={`${d.beneficiaire?.adresse || '—'}${d.beneficiaire?.cp_ville ? ' — ' + d.beneficiaire.cp_ville : ''}`} />
            <SummaryItem label="Date d'engagement" value={fmtDate(d.engagement_date)} />
            <SummaryItem label="Délégataire"
              value={d.selected_delegataire?.delegataire_nom || '—'}
              sub={d.selected_tarif_type === 'precarite' ? 'Coup de pouce précarité' : 'Classique CEE'} />
            <SummaryItem label="kWh cumac estimés" value={fmtKwh(d.kwh_cumac)} />
            <SummaryItem label="Prime CEE"
              value={<span className="text-emerald-700 font-bold text-lg">{fmtEUR(d.prime_eur)}</span>} />
          </div>
        </div>

        {/* Aperçu document */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#004AAD]" />
              <h2 className="font-bold text-slate-900">Document à signer</h2>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowPdf(!showPdf)}
              data-testid="aoh-toggle-pdf">
              <Eye className="w-4 h-4 mr-1.5" />
              {showPdf ? 'Masquer' : 'Afficher le PDF complet'}
            </Button>
          </div>
          {showPdf && (
            <iframe
              src={signUrl}
              title="Aperçu AoH"
              className="w-full"
              style={{ height: '70vh' }}
              data-testid="aoh-pdf-iframe"
            />
          )}
          {!showPdf && (
            <div className="p-6 bg-slate-50 text-sm text-slate-600 space-y-3">
              <p>
                En signant ci-dessous, vous certifiez sur l'honneur :
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>être le bénéficiaire de l'opération d'économies d'énergie décrite ;</li>
                <li>reconnaître le rôle actif du délégataire dans le montage du dossier CEE avant tout engagement ;</li>
                <li>vous engager à fournir toutes les pièces justificatives et autoriser les contrôles sur site ;</li>
                <li>n'avoir signé aucun autre contrat CEE avec un tiers pour cette même opération (règle de non-cumul).</li>
              </ul>
              <p className="text-xs text-slate-500 italic">
                Cliquez sur "Afficher le PDF complet" pour lire l'intégralité de l'attestation avant signature.
              </p>
            </div>
          )}
        </div>

        {/* Formulaire signature */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h2 className="font-bold text-slate-900">Votre signature</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Nom du signataire *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Prénom Nom" data-testid="aoh-input-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Qualité</label>
              <Input value={qualite} onChange={(e) => setQualite(e.target.value)}
                placeholder="Bénéficiaire, Gérant…" data-testid="aoh-input-qualite" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Fait à</label>
              <Input value={place} onChange={(e) => setPlace(e.target.value)}
                placeholder="Ville" data-testid="aoh-input-place" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              Signature manuscrite *
            </label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 relative"
                  style={{ height: 180 }}>
              <canvas
                ref={canvasRef}
                className="w-full h-full cursor-crosshair touch-none rounded-lg"
                onMouseDown={startDraw} onMouseMove={draw}
                onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw}
                onTouchEnd={stopDraw}
                data-testid="aoh-signature-canvas"
              />
              <button
                onClick={clearSig}
                className="absolute top-2 right-2 text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 bg-white/80 px-2 py-1 rounded"
                data-testid="aoh-clear-signature"
              >
                <RefreshCw className="w-3 h-3" /> Effacer
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Signez au doigt (mobile) ou à la souris (ordinateur).
            </p>
          </div>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="aoh-consent">
            <input
              type="checkbox"
              checked={accept}
              onChange={(e) => setAccept(e.target.checked)}
              className="mt-0.5"
              data-testid="aoh-consent-checkbox"
            />
            <span className="text-sm text-slate-700">
              Je certifie sur l'honneur l'exactitude des informations ci-dessus et j'accepte
              les déclarations de l'attestation. Je consens à la signature électronique de ce
              document (valeur probatoire eIDAS simple).
            </span>
          </label>

          <div className="flex justify-end pt-2">
            <Button
              onClick={submit}
              disabled={submitting || !name || !accept}
              className="bg-[#004AAD] hover:bg-[#003a8a] text-white"
              data-testid="aoh-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signature en cours…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Signer l'attestation</>
              )}
            </Button>
          </div>
        </div>

        <p className="text-[11px] text-center text-slate-400">
          Piste d'audit : IP, user-agent, horodatage et empreinte SHA-256 de la signature
          sont enregistrés sur le PDF signé. Lien valide jusqu'au {fmtDate(d.expires_at)}.
        </p>
      </div>
    </div>
  );
}

function SummaryItem({ label, value, sub }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="text-sm text-slate-900 font-medium">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
